import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { ZagIcon } from '../icon';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';
import { TooltipDirective } from '../tooltip/tooltip.directive';

const NON_TOGGLING_SELECTOR = '.zag-accordion-item__actions, .zag-accordion-item__title-actions';

/**
 * AccordionItemComponent - Individual collapsible section within an accordion
 *
 * Each item manages its own expanded state independently, allowing multiple items
 * to be open simultaneously. Supports icons, title, badge counter, and ARIA attributes
 * for accessibility.
 *
 * The `icon` slot accepts projected content for custom icons (e.g. SVG provider logos).
 * When no content is projected into the `icon` slot, the `icon` input is used as a
 * Material Icon name instead.
 *
 * The `title-actions` slot renders controls right after the title and before the badge;
 * clicks and Enter/Space inside it never toggle the item.
 *
 * @example Basic usage with material icon
 * ```html
 * <zag-accordion-item [title]="'Address'" icon="location_on">
 *   <p>Address fields go here</p>
 * </zag-accordion-item>
 * ```
 *
 * @example With custom SVG icon via slot
 * ```html
 * <zag-accordion-item [title]="'Microsoft'">
 *   <img slot="icon" src="..." alt="" />
 *   Fields here
 * </zag-accordion-item>
 * ```
 *
 * @example With status icon (e.g. checkmark when valid)
 * ```html
 * <zag-accordion-item [title]="'SMTP'" [statusIcon]="isValid ? 'check_circle' : null">
 *   Fields here
 * </zag-accordion-item>
 * ```
 *
 * @example With badge counter
 * ```html
 * <zag-accordion-item [title]="'Contacts'" icon="phone" [badge]="contacts.length">
 *   @for (contact of contacts; track contact.value) { <div>{{ contact.value }}</div> }
 * </zag-accordion-item>
 * ```
 */
@Component({
  selector: 'zag-accordion-item',
  templateUrl: './accordion-item.html',
  styleUrl: './accordion-item.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ZagIcon, ScrollIntoViewDirective, TooltipDirective],
})
export class AccordionItemComponent {
  private static nextId = 0;

  /**
   * Unique ID for aria-controls and content element
   */
  readonly contentId = `zag-accordion-content-${AccordionItemComponent.nextId++}`;

  /**
   * Material icon name to display before title (optional).
   * Ignored when content is projected into the `icon` slot.
   */
  readonly icon = input<string | null>(null);

  /**
   * Title text displayed in the toggle button
   */
  readonly title = input.required<string>();

  /**
   * Secondary line rendered under the title (optional)
   */
  readonly subtitle = input<string | null>(null);

  /**
   * Badge counter displayed after title (optional)
   * Set to null to hide badge
   */
  readonly badge = input<number | null>(null);

  /**
   * Optional trailing status icon (Material Icon name).
   * Shown between the badge and the chevron.
   * Use e.g. 'check_circle' to indicate a valid/complete state.
   */
  readonly statusIcon = input<string | null>(null);

  /**
   * Colour variant for the status icon.
   * 'success' renders in green, 'warning' in amber.
   * @default 'success'
   */
  readonly statusVariant = input<'success' | 'warning'>('success');

  readonly statusTooltip = input<string | null>(null);

  /**
   * Expanded state of the accordion item
   * Supports two-way binding via model()
   * @default false
   */
  readonly expanded = model(false);

  /**
   * When true, the accordion item cannot be expanded or collapsed via click
   * or keyboard, and renders in a visually muted state. The projected content
   * still exists in the template but stays hidden.
   * @default false
   */
  readonly disabled = input(false);

  /**
   * When true, renders without border, padding, or toggle header — always expanded.
   * Use inside dialogs where the accordion chrome is unwanted.
   * @default false
   */
  readonly flat = input(false);

  readonly headerVariant = input<'default' | 'collection' | 'stacked'>('default');

  readonly disableAutoScroll = input(false);

  /**
   * Toggle expanded state. Swallows clicks that originated inside the
   * `[slot=actions]` area so projected controls (toggles, buttons) don't
   * accidentally expand/collapse the accordion on click.
   */
  toggle(event?: Event): void {
    if (this.disabled()) return;
    const target = event?.target as HTMLElement | undefined;
    if (target?.closest(NON_TOGGLING_SELECTOR)) return;
    this.expanded.update(v => !v);
  }

  /**
   * Handle keyboard activation (Enter / Space) on the role="button" header.
   * Space would otherwise scroll the page, so we preventDefault.
   * Typed as `Event` because Angular's `(keydown.enter)` binding widens it.
   */
  handleToggleKey(event: Event): void {
    if (this.disabled()) return;
    // Ignore keyboard events that originated inside the actions slots.
    const target = event.target as HTMLElement | undefined;
    if (target?.closest(NON_TOGGLING_SELECTOR)) return;
    event.preventDefault();
    this.expanded.update(v => !v);
  }
}
